<template>
    <div class="flex flex-col gap-6 proxima_nova w-[80%]">
      <div v-if="!isLoading && accounts.length > 0" class="flex flex-row justify-between items-center">
        <span class="text-lg font-semibold text-color">
          Connected accounts
        </span>
        <a-button
          class="primary_medium"
          @click="handleConnectAccount"
        >
          Connect Account
        </a-button>
      </div>

      <div v-if="isLoading" class="flex flex-col items-center justify-center gap-2 mt-20">
        <CustomLoader :size="28" />
      </div>

      <div v-else>
        <SimpleTable
          v-if="accounts.length > 0"
          :headers="whatsappHeaders"
          :data="accounts"
          :show-actions-column="true"
          :actions="actions"
          :custom-column-render="customColumnRender"
          :custom-row="handleRowClick"
          disable-horizontal-scroll
        />

        <EmptyScreen
          v-else
          source="whatsapp_accounts"
          :text="`Connect Your WhatsApp Business Account`"
          :description="`Start by connecting your WhatsApp Business account to manage your templates and communications.`"
          :type="emptyScreenTypes.DEFAULT"
          padding="mt-20"
        >
          <a-button
            class="primary_medium"
            @click="handleConnectAccount"
          >
            Connect Account
          </a-button>
        </EmptyScreen>
      </div>

      <ConfirmationModal
        v-model:visible="isDeleteModalVisible"
        title="Delete WhatsApp Account"
        :message="deleteConfirmMessage"
        type="delete"
        confirm-text="Delete"
        cancel-text="Cancel"
        loading-text="Deleting..."
        :loading="isDeleting"
        @confirm="handleDeleteConfirm"
        @cancel="handleDeleteCancel"
      />

      <ConfirmationModal
        v-model:visible="isConnectModalVisible"
        title="Connect WhatsApp Account"
        :message="connectConfirmMessage"
        type="default"
        confirm-text="Connect"
        cancel-text="Cancel"
        loading-text="Connecting..."
        :loading="isConnecting"
        @confirm="handleConnectConfirm"
        @cancel="handleConnectCancel"
      />

      <ConfirmationModal
        v-model:visible="isDisconnectModalVisible"
        title="Disconnect WhatsApp Account"
        :message="disconnectConfirmMessage"
        type="danger"
        confirm-text="Disconnect"
        cancel-text="Cancel"
        loading-text="Disconnecting..."
        :loading="isDisconnecting"
        @confirm="handleDisconnectConfirm"
        @cancel="handleDisconnectCancel"
      />
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { message } from 'ant-design-vue';
  import SimpleTable from '@/src/components/custom_table_components/SimpleTable.vue';
  import StatusTagProvider from '@/src/components/StatusTagProvider.vue';
  import CustomLoader from '@/src/components/CustomLoader.vue';
  import EmptyScreen from '@/src/components/EmptyScreen.vue';
  import ConfirmationModal from '@/src/components/ConfirmationModal.vue';
  import { whatsappHeaders as baseWhatsappHeaders } from '@/src/constants/whatsappConstants.js';
  import { imageNames } from '@/src/constants/imageConstants.js';
  import { emptyScreenTypes } from '@/src/constants/searchConstants.js';
  import IntegrationService from '@/src/api/IntegrationService.js';

  const router = useRouter();
  
  const accounts = ref([]);
  const isLoading = ref(false);
  const isDeleteModalVisible = ref(false);
  const accountToDelete = ref(null);
  const isDeleting = ref(false);

  const isConnectModalVisible = ref(false);
  const accountToConnect = ref(null);
  const isConnecting = ref(false);

  const isDisconnectModalVisible = ref(false);
  const accountToDisconnect = ref(null);
  const isDisconnecting = ref(false);

  const whatsappHeaders = computed(() => baseWhatsappHeaders);

  const deleteConfirmMessage = computed(() => {
    if (!accountToDelete.value) return '';
    return `Are you sure you want to delete the WhatsApp account with phone number ${accountToDelete.value.phone_number}?`;
  });

  const connectConfirmMessage = computed(() => {
    if (!accountToConnect.value) return '';
    return `Are you sure you want to connect the WhatsApp account with phone number ${accountToConnect.value.phone_number}?`;
  });

  const disconnectConfirmMessage = computed(() => {
    if (!accountToDisconnect.value) return '';
    return `Are you sure you want to disconnect the WhatsApp account with phone number ${accountToDisconnect.value.phone_number}? This may affect active campaigns using this account.`;
  });
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatVendorType = (vendorType) => {
    if (!vendorType) return '';
    return vendorType.toUpperCase();
  };

  const formatConnectedStatus = (connected) => {
    return connected ? 'connected' : 'disconnected';
  };

  const showConnectModal = (row) => {
    accountToConnect.value = row;
    isConnectModalVisible.value = true;
  };

  const handleConnectConfirm = async () => {
    if (!accountToConnect.value) return;
    isConnecting.value = true;
    try {
      await IntegrationService.updateWhatsAppConfigConnected(accountToConnect.value.id, true);
      message.success("Account connected successfully");
      await fetchAccounts();
    } catch (error) {
      console.error('Error connecting account:', error);
      message.error(error.response?.data?.message || "Failed to connect account");
    } finally {
      isConnecting.value = false;
      isConnectModalVisible.value = false;
      accountToConnect.value = null;
    }
  };

  const handleConnectCancel = () => {
    isConnectModalVisible.value = false;
    accountToConnect.value = null;
  };

  const showDisconnectModal = (row) => {
    accountToDisconnect.value = row;
    isDisconnectModalVisible.value = true;
  };

  const handleDisconnectConfirm = async () => {
    if (!accountToDisconnect.value) return;
    isDisconnecting.value = true;
    try {
      await IntegrationService.updateWhatsAppConfigConnected(accountToDisconnect.value.id, false);
      message.success("Account disconnected successfully");
      await fetchAccounts();
    } catch (error) {
      console.error('Error disconnecting account:', error);
      message.error(error.response?.data?.message || "Failed to disconnect account");
    } finally {
      isDisconnecting.value = false;
      isDisconnectModalVisible.value = false;
      accountToDisconnect.value = null;
    }
  };

  const handleDisconnectCancel = () => {
    isDisconnectModalVisible.value = false;
    accountToDisconnect.value = null;
  };

  const checkTemplatesAndDelete = async (row) => {
    try {
      const response = await IntegrationService.fetchWhatsAppTemplates({
        page: 1,
        size: 1,
        config_id: row.id,
      });
      if (!response.success) {
        message.error('Failed to verify account status. Please try again.');
        return;
      }
      if (response.total_count > 0) {
        message.error("Please delete all templates before removing this account");
        return;
      }
      accountToDelete.value = row;
      isDeleteModalVisible.value = true;
    } catch (error) {
      console.error('Error checking templates:', error);
      message.error('Failed to verify account status. Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete.value) return;
    isDeleting.value = true;
    try {
      const response = await IntegrationService.deleteWhatsAppConfig(accountToDelete.value.id);
      if (response.success) {
        message.success("Account deleted successfully");
        await fetchAccounts();
      } else {
        message.error(response.error || "Failed to delete account");
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      message.error(error.response?.data?.message || "Failed to delete account");
    } finally {
      isDeleting.value = false;
      isDeleteModalVisible.value = false;
      accountToDelete.value = null;
    }
  };

  const handleDeleteCancel = () => {
    isDeleteModalVisible.value = false;
    accountToDelete.value = null;
  };

  const actions = [
    {
      label: "Connect",
      handler: (row) => {
        if (!row.connected) showConnectModal(row);
      },
      icon: imageNames.LINK_GREY,
      condition: (row) => !row.connected,
    },
    {
      label: "Disconnect",
      handler: (row) => {
        if (row.connected) showDisconnectModal(row);
      },
      icon: imageNames.DISCONNECT_GREY,
      condition: (row) => row.connected,
    },
    {
      label: "Delete Account",
      handler: (row) => checkTemplatesAndDelete(row),
      icon: imageNames.DELETE_GREY,
    },
  ];

  const customColumnRender = {
    status: StatusTagProvider,
  };

  const fetchAccounts = async () => {
    isLoading.value = true;
    try {
      const response = await IntegrationService.fetchWhatsAppConfigs();
      if (response.success) {
        const configs = response.configs || [];
        accounts.value = configs.map((config) => ({
          ...config,
          vendor_type: formatVendorType(config.vendor_type),
          status: formatConnectedStatus(config.connected),
          created_at: formatDate(config.created_at),
        }));
      } else {
        accounts.value = [];
      }
    } catch (error) {
      console.error('Error fetching WhatsApp accounts:', error);
      message.error('Failed to fetch WhatsApp accounts');
      accounts.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(() => {
    fetchAccounts();
  });
  
  const handleConnectAccount = () => {
    router.push({ name: 'whatsapp-connect-account' });
  };

  const handleRowClick = (record) => {
    const isConnected = record.connected;
    return {
      style: { cursor: isConnected ? 'pointer' : 'not-allowed' },
      onClick: (event) => {
        // Don't navigate if clicking on the actions column
        if (event.target.closest('.action-dropdown-trigger') || 
            event.target.closest('.ant-dropdown')) {
          return;
        }
        // Don't navigate if account is disconnected
        if (!isConnected) {
          return;
        }
        router.push(`/settings/integrations/whatsapp/templates_table/${record.id}`);
      }
    };
  };



  </script>
  
  <style scoped>
  /* Using Tailwind classes, but you can add custom styles here if needed */
  </style>